'use client';

import React from 'react';

import AppImage from './AppImage';

interface AppLogoProps {
  src?: string;
  text?: string;
  iconName?: string;
  size?: number;
  className?: string;
  onClick?: () => void;
  variant?: 'default' | 'icon-only' | 'horizontal';
}

function AppLogo({
  src = '/assets/images/helium_logo-1771846260914.png',
  text,
  iconName = 'SparklesIcon',
  size = 64,
  className = '',
  onClick,
  variant = 'default',
}: AppLogoProps) {
  // Premium SVG Logo with smooth curves and perfect symmetry
  const PremiumLogoIcon = () => (
    <svg
      width={size}
      height={size}
      viewBox="0 0 100 100"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="flex-shrink-0"
    >
      {/* Outer ring with smooth curves */}
      <circle
        cx="50"
        cy="50"
        r="45"
        stroke="url(#logo-gradient)"
        strokeWidth="3"
        fill="none"
        opacity="0.6"
      />
      
      {/* Inner geometric shape - refined H symbol */}
      <path
        d="M 30 30 L 30 70 M 30 50 L 70 50 M 70 30 L 70 70"
        stroke="url(#logo-gradient)"
        strokeWidth="4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      
      {/* Center accent dot */}
      <circle
        cx="50"
        cy="50"
        r="4"
        fill="url(#logo-gradient)"
        className="animate-glow-pulse"
      />
      
      {/* Gradient definitions */}
      <defs>
        <linearGradient id="logo-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#5B2EFF" />
          <stop offset="50%" stopColor="#7C4DFF" />
          <stop offset="100%" stopColor="#9F7AEA" />
        </linearGradient>
      </defs>
    </svg>
  );

  return (
    <div
      className={`flex items-center gap-3 ${onClick ? 'cursor-pointer hover:opacity-90 transition-smooth' : ''} ${className}`}
      onClick={onClick}
    >
      {/* Logo Icon */}
      {variant === 'icon-only' ? (
        <PremiumLogoIcon />
      ) : src ? (
        <div className="relative bg-transparent">
          <AppImage 
            src={src} 
            alt="Helium Logo" 
            width={size} 
            height={size} 
            className="flex-shrink-0" 
            unoptimized={true}
            style={{
              filter: 'brightness(1.1) contrast(1.05)',
              opacity: 0.95,
              background: 'transparent'
            }}
          />
        </div>
      ) : (
        <PremiumLogoIcon />
      )}

      {/* Logo Text */}
      {text && variant !== 'icon-only' && (
        <span className="text-xl font-bold tracking-tight gradient-text">
          {text}
        </span>
      )}
    </div>
  );
}

export default AppLogo;
